import { zodResolver } from "@hookform/resolvers/zod";
import { RefreshCwIcon, SparklesIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { GenerateRubricInfoGuide } from "./GenerateRubricInfoGuide";
import { InfoBox, KeyboardShortcut, LoadingIndicator } from "@/components";
import { FormRichTextInput } from "@/components/form/FormRichTextInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";

import { useErrorToast } from "@/hooks/useErrorToast";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { useUpdateSetJobInternalConfig } from "@/hooks/updateSets/useUpdateSetJobInternalConfig";
import { JobInternalConfig as JobInternalConfigType } from "@/types";

const humanInputSchema = z.object({
  autoProfileRatingHumanInput: z.string().nullable(),
});

export const GenerateRubricModal = ({
  isRegenerate,
  jobInternalConfig,
  onClose,
}: {
  isRegenerate: boolean;
  jobInternalConfig: JobInternalConfigType;
  onClose: () => void;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: updateSetJobInternalConfig, isPending } =
    useUpdateSetJobInternalConfig();

  /* Misc Hooks */
  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  const form = useForm({
    defaultValues: {
      autoProfileRatingHumanInput:
        jobInternalConfig.autoProfileRatingHumanInput ?? "",
    },
    resolver: zodResolver(humanInputSchema),
  });

  /* Handlers */
  const handleSubmit = (data: z.infer<typeof humanInputSchema>) => {
    updateSetJobInternalConfig(
      {
        id: jobInternalConfig.id,
        updateRequest: {
          autoProfileRatingHumanInput: data.autoProfileRatingHumanInput,
          autoProfileRatingRubric: null, // Null out the rubric so the backend will regenerate it
        },
      },
      {
        onSuccess: () => {
          successToast({
            description: isRegenerate
              ? "Rubric regenerated!"
              : "Rubric generated!",
          });
          onClose();
        },
        onError: () => {
          errorToast({
            description: isRegenerate
              ? "Failed to regenerate rubric"
              : "Failed to generate rubric",
          });
        },
      },
    );
  };

  /* Render */
  const render = () => {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-h-[80vh] sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {isRegenerate ? "Regenerate Rubric" : "Generate Rubric"}
            </DialogTitle>
            {isRegenerate && (
              <DialogDescription>
                Edit your human input below and regenerate a completely new
                rubric.
              </DialogDescription>
            )}
          </DialogHeader>

          {isRegenerate && (
            <InfoBox variant="warning">
              This will{" "}
              <span className="font-semibold">completely replace</span> the
              current rubric. Any manual edits you&apos;ve made directly to the
              rubric will be lost.
            </InfoBox>
          )}

          <Form {...form}>
            <form
              className="flex flex-col gap-4"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.metaKey && !e.shiftKey) {
                  e.preventDefault();
                }
                if (e.metaKey && e.key === "Enter") {
                  e.preventDefault();
                  void form.handleSubmit(handleSubmit)();
                }
              }}
            >
              <FormRichTextInput
                editorHeight="180px"
                fieldName="autoProfileRatingHumanInput"
                form={form}
                label={
                  <span className="inline-flex items-center gap-1">
                    Human Input
                    <GenerateRubricInfoGuide />
                  </span>
                }
              />
              <DialogFooter>
                <Button onClick={onClose} type="button" variant="outline">
                  Cancel
                </Button>
                <Button
                  disabled={isPending}
                  onClick={() => {
                    void form.handleSubmit(handleSubmit)();
                  }}
                  type="button"
                >
                  {isPending ? (
                    <LoadingIndicator gearColor="white" variant="small" />
                  ) : isRegenerate ? (
                    <RefreshCwIcon className="h-4 w-4" />
                  ) : (
                    <SparklesIcon className="h-4 w-4" />
                  )}
                  {isRegenerate ? "Regenerate Rubric" : "Generate Rubric"}
                  <KeyboardShortcut text="⌘↵" />
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    );
  };

  return render();
};
